import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicIcon from '@mui/icons-material/Mic';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../../services/api.js';
import AudioRecorder from '../../components/AudioRecorder.jsx';
import IvrIntroManager from '../ivr/IvrIntroManager.jsx';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';

const KEY_LABELS = {
  wife_exam_encourage: 'פידבק אחרי בחינה',
  registration_approved: 'אישור רישום',
  general: 'כללי',
  broadcast: 'שיגור הודעה',
};

const DEFAULT_WIFE_NAME = 'פידבק אחרי בחינה';
const DEFAULT_REG_NAME = 'אישור רישום';
const DEFAULT_REG_TTS = 'הרישום למערכת {systemName} הושלם בהצלחה';

/** שמות ישנים → תצוגה חדשה */
function displayTemplateName(name) {
  const n = String(name || '').trim();
  if (!n) return DEFAULT_WIFE_NAME;
  if (/עידוד\s*לאשה/i.test(n)) return DEFAULT_WIFE_NAME;
  return n;
}

function apiErrorMessage(e, fallback = 'שגיאה') {
  const data = e?.response?.data;
  if (!data) return e?.message || fallback;
  if (Array.isArray(data.details) && data.details.length) {
    return `${data.error || fallback}: ${data.details.join(', ')}`;
  }
  return data.error || e.message || fallback;
}

const RESEND_REASON_LABELS = {
  no_log: 'לא נשלח כלל',
  failed: 'נכשל',
  skipped: 'דולג',
  not_heard: 'לא ענתה',
  unknown: 'נשלח, לא ידוע אם נשמע',
  stuck: 'תקוע',
};

function daysAgoIso(days) {
  const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

const panelSx = {
  mb: 3,
  p: 2.5,
  borderRadius: 2,
  bgcolor: 'var(--ck-surface, #fff)',
  border: '1px solid',
  borderColor: 'divider',
};

const IvrTemplates = () => {
  const inNextDesign = useCkInside();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() =>
    new URLSearchParams(location.search).get('tab') === 'intro' ? 'intro' : 'templates'
  );
  const [templates, setTemplates] = useState([]);
  const [automation, setAutomation] = useState(null);
  const [regAutomation, setRegAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAuto, setSavingAuto] = useState(false);
  const [savingReg, setSavingReg] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState(DEFAULT_WIFE_NAME);
  const [newKey, setNewKey] = useState('wife_exam_encourage');
  const [creating, setCreating] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [recordTpl, setRecordTpl] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testKind, setTestKind] = useState('wife'); // 'wife' | 'reg'
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [resendSince, setResendSince] = useState(() => daysAgoIso(7));
  const [resendUnknown, setResendUnknown] = useState(false);
  const [pending, setPending] = useState(null);
  const [checkingPending, setCheckingPending] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const audioElRef = useRef(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    const next = new URLSearchParams(location.search).get('tab') === 'intro' ? 'intro' : 'templates';
    setTab(next);
  }, [location.search]);

  const handleTabChange = (_e, value) => {
    setTab(value);
    navigate(value === 'intro' ? '/ivr-templates?tab=intro' : '/ivr-templates', { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [tRes, aRes, rRes] = await Promise.all([
        api.get('/ivr-templates'),
        api.get('/ivr-automations/wife-exam-encourage'),
        api.get('/ivr-automations/registration-approved'),
      ]);
      const rows = (tRes.data?.data || []).map((t) =>
        /עידוד\s*לאשה/i.test(String(t.name || ''))
          ? { ...t, name: DEFAULT_WIFE_NAME }
          : t
      );
      setTemplates(rows);
      setAutomation(aRes.data || null);
      setRegAutomation(rRes.data || null);
      (tRes.data?.data || [])
        .filter((t) => /עידוד\s*לאשה/i.test(String(t.name || '')))
        .forEach((t) => {
          api.patch(`/ivr-templates/${t.id}`, { name: DEFAULT_WIFE_NAME }).catch(() => {});
        });
    } catch (e) {
      setErr(apiErrorMessage(e, 'שגיאה בטעינה'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current = null;
      }
    },
    []
  );

  const saveAutomation = async (patch) => {
    setSavingAuto(true);
    setMsg('');
    setErr('');
    try {
      const { data } = await api.put('/ivr-automations/wife-exam-encourage', {
        enabled: automation?.enabled,
        templateId: automation?.templateId,
        ...patch,
      });
      setAutomation(data);
      setMsg(
        data.enabled
          ? 'האוטומציה נשמרה. מבחנים פעילים עודכנו לחזרה ל־/77/exam_done אחרי סיום.'
          : 'האוטומציה עודכנה'
      );
    } catch (e) {
      setErr(apiErrorMessage(e, 'שגיאה בשמירה'));
      await load();
    } finally {
      setSavingAuto(false);
    }
  };

  const saveRegAutomation = async (patch) => {
    setSavingReg(true);
    setMsg('');
    setErr('');
    try {
      const { data } = await api.put('/ivr-automations/registration-approved', {
        enabled: regAutomation?.enabled,
        mode: regAutomation?.mode || 'tts',
        ttsText: regAutomation?.ttsText || DEFAULT_REG_TTS,
        templateId: regAutomation?.templateId,
        ...patch,
      });
      setRegAutomation(data);
      setMsg('אוטומציית אישור רישום עודכנה');
    } catch (e) {
      setErr(apiErrorMessage(e, 'שגיאה בשמירה'));
      await load();
    } finally {
      setSavingReg(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setErr('');
    try {
      const desc =
        newKey === 'registration_approved'
          ? 'הקלטה להודעת אישור רישום — שיחה יוצאת אחרי אישור חבר חדש'
          : 'הקלטה לפידבק אחרי בחינה — שיחה יוצאת אחרי סיום מבחן';
      await api.post('/ivr-templates', {
        name: newName.trim(),
        key: newKey,
        description: desc,
      });
      setCreateOpen(false);
      setNewName(DEFAULT_WIFE_NAME);
      setNewKey('wife_exam_encourage');
      await load();
      setMsg('התבנית נוצרה — כעת העלו הקלטה');
    } catch (e) {
      setErr(apiErrorMessage(e, 'שגיאה ביצירת תבנית'));
      await load();
    } finally {
      setCreating(false);
    }
  };

  const uploadBlob = async (templateId, blob, filename) => {
    setUploadingId(templateId);
    setErr('');
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('audio', blob, filename || 'recording.webm');
      await api.post(`/ivr-templates/${templateId}/audio`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecordTpl(null);
      await load();
      setMsg('ההקלטה הועלתה וסונכרנה לימות');
    } catch (e) {
      setErr(apiErrorMessage(e, 'שגיאה בהעלאה'));
    } finally {
      setUploadingId(null);
    }
  };

  const onFilePick = (tpl, file) => {
    if (!file) return;
    uploadBlob(tpl.id, file, file.name);
  };

  const playAudio = async (tpl) => {
    if (playingId === tpl.id) {
      audioElRef.current?.pause();
      setPlayingId(null);
      return;
    }
    try {
      if (audioElRef.current) {
        audioElRef.current.pause();
      }
      const { data } = await api.get(`/ivr-templates/${tpl.id}/audio`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const el = new Audio(url);
      audioElRef.current = el;
      el.onended = () => {
        setPlayingId(null);
        URL.revokeObjectURL(url);
      };
      await el.play();
      setPlayingId(tpl.id);
    } catch (e) {
      setErr(e.response?.data?.error || 'לא ניתן להשמיע');
    }
  };

  const openTest = (kind) => {
    setTestKind(kind);
    setTestOpen(true);
  };

  const runTestCall = async () => {
    if (!testPhone.trim()) return;
    setTesting(true);
    setErr('');
    setMsg('');
    try {
      const path =
        testKind === 'reg'
          ? '/ivr-automations/registration-approved/test'
          : '/ivr-automations/wife-exam-encourage/test';
      const { data } = await api.post(path, { phone: testPhone.trim() });
      setMsg(`שיחת בדיקה נשלחה (${data.result?.phone || testPhone})`);
      setTestOpen(false);
    } catch (e) {
      setErr(apiErrorMessage(e, 'שיחת הבדיקה נכשלה'));
    } finally {
      setTesting(false);
    }
  };

  const checkPending = async () => {
    setCheckingPending(true);
    setErr('');
    try {
      const { data } = await api.get('/ivr-automations/wife-exam-encourage/pending', {
        params: { since: resendSince, includeUnknown: resendUnknown },
      });
      setPending(data);
      if (data.status) setResendStatus(data.status);
    } catch (e) {
      setErr(apiErrorMessage(e, 'שגיאה בבדיקת ממתינות'));
    } finally {
      setCheckingPending(false);
    }
  };

  const runResend = async () => {
    setResendConfirmOpen(false);
    setErr('');
    setMsg('');
    try {
      const { data } = await api.post('/ivr-automations/wife-exam-encourage/resend', {
        since: resendSince,
        includeUnknown: resendUnknown,
      });
      setResendStatus(data.status);
    } catch (e) {
      setErr(apiErrorMessage(e, 'שליחה חוזרת נכשלה'));
    }
  };

  // מעקב התקדמות בזמן שליחה חוזרת
  useEffect(() => {
    if (!resendStatus?.running) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get('/ivr-automations/wife-exam-encourage/resend');
        setResendStatus(data.status);
        if (!data.status?.running) {
          setPending(null);
          load();
        }
      } catch {
        /* ננסה שוב בסיבוב הבא */
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [resendStatus?.running, load]);

  const wifeTemplates = templates.filter((t) => t.key === 'wife_exam_encourage');
  const regTemplates = templates.filter((t) => t.key === 'registration_approved');
  const regMode = ['recording', 'tzintuk'].includes(regAutomation?.mode) ? regAutomation.mode : 'tts';

  return (
    <Box sx={inNextDesign ? { maxWidth: 960, width: '100%' } : { p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <AdminPageHeader
        title="תבניות IVR"
        sub={tab === 'intro' ? 'פתיח הקו' : 'תבניות שלוחות והודעות'}
        legacy={(
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <PhoneInTalkOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              תבניות IVR
            </Typography>
          </Stack>
        )}
      />

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="תבניות" value="templates" />
        <Tab label="פתיח" value="intro" />
      </Tabs>

      {tab === 'intro' ? (
        <IvrIntroManager embedded />
      ) : loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
      <>
      {msg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
          {err}
        </Alert>
      )}

      {/* אוטומציה — פידבק בחינה */}
      <Box sx={panelSx}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              פידבק בחינה
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              יוצא לטלפון האשה (שדה «טלפון אשה» בכרטיס החבר) אחרי שהבעל מסיים מבחן — בפורטל או בטלפון.
              בשעות השקטות (22:00–10:00) השיחה נדחית ויוצאת ב-10:00 בקמפיין אחד.
            </Typography>
          </Box>
          <FormControlLabel
            sx={{
              m: 0,
              flexShrink: 0,
              alignItems: 'center',
              gap: 0.5,
              '& .MuiFormControlLabel-label': {
                fontSize: 14,
                fontWeight: 600,
                color: automation?.enabled ? 'success.main' : 'text.secondary',
                minWidth: 36,
              },
            }}
            labelPlacement="start"
            control={
              <Switch
                checked={Boolean(automation?.enabled)}
                disabled={savingAuto}
                color="success"
                onChange={(e) => {
                  if (e.target.checked) {
                    const selected = wifeTemplates.find(
                      (t) => t.id === automation?.templateId
                    );
                    if (!selected?.yemotSynced) {
                      setErr(
                        'קודם צרו תבנית, העלו הקלטה (עד «מסונכרן לימות»), בחרו אותה ברשימה — ואז הפעילו'
                      );
                      return;
                    }
                  }
                  saveAutomation({ enabled: e.target.checked });
                }}
              />
            }
            label={automation?.enabled ? 'פעיל' : 'כבוי'}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          spacing={1.5}
        >
          <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel>תבנית להשמעה</InputLabel>
            <Select
              label="תבנית להשמעה"
              value={automation?.templateId || ''}
              disabled={savingAuto}
              onChange={(e) =>
                saveAutomation({ templateId: e.target.value || null })
              }
            >
              <MenuItem value="">
                <em>ללא</em>
              </MenuItem>
              {wifeTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id} disabled={!t.yemotSynced}>
                  {displayTemplateName(t.name)}
                  {!t.yemotSynced ? ' (חסרה הקלטה מסונכרנת)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            <Button
              variant="outlined"
              size="medium"
              disabled={!automation?.templateId || savingAuto}
              onClick={() => openTest('wife')}
            >
              שיחת בדיקה
            </Button>
            {savingAuto && <CircularProgress size={22} />}
            {automation?.successCount > 0 && (
              <Chip size="small" label={`נשלחו: ${automation.successCount}`} />
            )}
            {automation?.failureCount > 0 && (
              <Chip size="small" color="warning" label={`כשלים: ${automation.failureCount}`} />
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle1" fontWeight={700}>
          שליחה חוזרת
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          שולח שוב לכל מי שהבעל סיים מבחן ולא קיבלה פידבק או לא ענתה. שיחה אחת לכל מספר.
          מי שלא ענתה וחוזרת למערכת תוך 72 שעות שומעת את ההודעה לפני התפריט.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          spacing={1.5}
        >
          <TextField
            type="date"
            size="small"
            label="מבחנים מתאריך"
            value={resendSince}
            onChange={(e) => {
              setResendSince(e.target.value);
              setPending(null);
            }}
            InputLabelProps={{ shrink: true }}
            disabled={resendStatus?.running}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={resendUnknown}
                onChange={(e) => {
                  setResendUnknown(e.target.checked);
                  setPending(null);
                }}
                disabled={resendStatus?.running}
              />
            }
            label="כולל שנשלחו לפני המעקב (לא ידוע אם נשמעו)"
          />
          <Button
            variant="outlined"
            onClick={checkPending}
            disabled={checkingPending || resendStatus?.running || !automation?.templateId}
          >
            {checkingPending ? <CircularProgress size={20} /> : 'בדוק מי ממתינה'}
          </Button>
        </Stack>

        {pending && !resendStatus?.running && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                {pending.stats.phones} מספרים ממתינים
              </Typography>
              {Object.entries(pending.stats.byReason || {}).map(([k, n]) => (
                <Chip key={k} size="small" label={`${RESEND_REASON_LABELS[k] || k}: ${n}`} />
              ))}
              {pending.stats.deferred > 0 && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`ממתינות לסוף השעות השקטות: ${pending.stats.deferred}`}
                />
              )}
              {pending.stats.heard > 0 && (
                <Chip size="small" color="success" variant="outlined" label={`שמעו: ${pending.stats.heard}`} />
              )}
              {pending.stats.noPhoneAlt > 0 && (
                <Chip size="small" variant="outlined" label={`ללא טלפון אשה: ${pending.stats.noPhoneAlt}`} />
              )}
            </Stack>
            <Button
              sx={{ mt: 1.5 }}
              variant="contained"
              color="warning"
              disabled={!pending.stats.phones}
              onClick={() => setResendConfirmOpen(true)}
            >
              שלח שוב צינתוק ל־{pending.stats.phones}
            </Button>
          </Box>
        )}

        {resendStatus && (
          <Box sx={{ mt: 2 }}>
            {resendStatus.running ? (
              <>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  שולח… {resendStatus.sent + resendStatus.failed} / {resendStatus.total}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    resendStatus.total
                      ? ((resendStatus.sent + resendStatus.failed) * 100) / resendStatus.total
                      : 100
                  }
                />
              </>
            ) : (
              <Alert severity={resendStatus.stoppedReason || resendStatus.failed ? 'warning' : 'success'}>
                שליחה חוזרת הסתיימה: נשלחו {resendStatus.sent}, נכשלו {resendStatus.failed} מתוך{' '}
                {resendStatus.total}
                {resendStatus.campaigns ? ` (${resendStatus.campaigns} קמפיינים בימות)` : ''}
                {resendStatus.stoppedReason ? ` — נעצרה: ${resendStatus.stoppedReason}` : ''}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <Dialog open={resendConfirmOpen} onClose={() => setResendConfirmOpen(false)}>
        <DialogTitle>שליחה חוזרת</DialogTitle>
        <DialogContent>
          <DialogContentText>
            יחויגו {pending?.stats.phones || 0} מספרים עם הודעת הפידבק. להמשיך?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendConfirmOpen(false)}>ביטול</Button>
          <Button variant="contained" color="warning" onClick={runResend}>
            שלח
          </Button>
        </DialogActions>
      </Dialog>

      {/* אוטומציה — אישור רישום */}
      <Box sx={panelSx}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              אישור רישום
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              שיחה יוצאת לנרשם אחרי אישור בקשת רישום. אפשר טקסט (TTS) או הקלטה.
              בקוד אישי — הוסיפו {'{personalCode}'} לטקסט רק אם רלוונטי.
            </Typography>
          </Box>
          <FormControlLabel
            sx={{
              m: 0,
              flexShrink: 0,
              alignItems: 'center',
              gap: 0.5,
              '& .MuiFormControlLabel-label': {
                fontSize: 14,
                fontWeight: 600,
                color: regAutomation?.enabled ? 'success.main' : 'text.secondary',
                minWidth: 36,
              },
            }}
            labelPlacement="start"
            control={
              <Switch
                checked={Boolean(regAutomation?.enabled)}
                disabled={savingReg}
                color="success"
                onChange={(e) => {
                  if (e.target.checked) {
                    if (regMode === 'tts') {
                      if (!(regAutomation?.ttsText || '').trim()) {
                        setErr('קודם הזינו טקסט להקראה — ואז הפעילו');
                        return;
                      }
                    } else if (regMode === 'recording') {
                      const selected = regTemplates.find(
                        (t) => t.id === regAutomation?.templateId
                      );
                      if (!selected?.yemotSynced) {
                        setErr(
                          'קודם צרו תבנית אישור רישום, העלו הקלטה, בחרו אותה — ואז הפעילו'
                        );
                        return;
                      }
                    }
                  }
                  saveRegAutomation({ enabled: e.target.checked });
                }}
              />
            }
            label={regAutomation?.enabled ? 'פעיל' : 'כבוי'}
          />
        </Stack>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={regMode}
            onChange={(e) => {
              const mode = e.target.value;
              saveRegAutomation({ mode, enabled: false });
            }}
          >
            <FormControlLabel
              value="tts"
              control={<Radio size="small" disabled={savingReg} />}
              label="טקסט (TTS)"
            />
            <FormControlLabel
              value="recording"
              control={<Radio size="small" disabled={savingReg} />}
              label="הקלטה"
            />
            <FormControlLabel
              value="tzintuk"
              control={<Radio size="small" disabled={savingReg} />}
              label="צינתוק"
            />
          </RadioGroup>
        </FormControl>

        {regMode === 'tzintuk' ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              צלצול קצר לנייד, בלי הודעה. נשלח לנייד בלבד ועולה 0.1 יחידה.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="medium"
                disabled={savingReg}
                onClick={() => openTest('reg')}
              >
                צינתוק בדיקה
              </Button>
              {savingReg && <CircularProgress size={22} />}
              {regAutomation?.successCount > 0 && (
                <Chip size="small" label={`נשלחו: ${regAutomation.successCount}`} />
              )}
              {regAutomation?.failureCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`כשלים: ${regAutomation.failureCount}`}
                />
              )}
            </Stack>
          </Stack>
        ) : regMode === 'tts' ? (
          <Stack spacing={1.5}>
            <TextField
              label="טקסט להקראה"
              value={regAutomation?.ttsText ?? DEFAULT_REG_TTS}
              onChange={(e) =>
                setRegAutomation((prev) => ({
                  ...prev,
                  ttsText: e.target.value,
                }))
              }
              onBlur={() =>
                saveRegAutomation({
                  ttsText: regAutomation?.ttsText || DEFAULT_REG_TTS,
                })
              }
              fullWidth
              multiline
              minRows={2}
              disabled={savingReg}
              helperText="מציינים: {systemName} · {personalCode} (אופציונלי)"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="medium"
                disabled={savingReg || !(regAutomation?.ttsText || '').trim()}
                onClick={() => openTest('reg')}
              >
                שיחת בדיקה
              </Button>
              {savingReg && <CircularProgress size={22} />}
              {regAutomation?.successCount > 0 && (
                <Chip size="small" label={`נשלחו: ${regAutomation.successCount}`} />
              )}
              {regAutomation?.failureCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`כשלים: ${regAutomation.failureCount}`}
                />
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel>תבנית להשמעה</InputLabel>
              <Select
                label="תבנית להשמעה"
                value={regAutomation?.templateId || ''}
                disabled={savingReg}
                onChange={(e) =>
                  saveRegAutomation({ templateId: e.target.value || null })
                }
              >
                <MenuItem value="">
                  <em>ללא</em>
                </MenuItem>
                {regTemplates.map((t) => (
                  <MenuItem key={t.id} value={t.id} disabled={!t.yemotSynced}>
                    {displayTemplateName(t.name)}
                    {!t.yemotSynced ? ' (חסרה הקלטה מסונכרנת)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              <Button
                variant="outlined"
                size="medium"
                disabled={!regAutomation?.templateId || savingReg}
                onClick={() => openTest('reg')}
              >
                שיחת בדיקה
              </Button>
              {savingReg && <CircularProgress size={22} />}
              {regAutomation?.successCount > 0 && (
                <Chip size="small" label={`נשלחו: ${regAutomation.successCount}`} />
              )}
              {regAutomation?.failureCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`כשלים: ${regAutomation.failureCount}`}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Box>

      {/* ספרייה */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6" fontWeight={700}>
          ספריית תבניות
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setCreateOpen(true)}
        >
          תבנית חדשה
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {templates.length === 0 && (
          <Typography color="text.secondary">אין תבניות עדיין — צרו אחת והעלו הקלטה.</Typography>
        )}
        {templates.map((tpl) => (
          <Box
            key={tpl.id}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'var(--ck-surface, #fff)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1.5}
            >
              <Box>
                <Typography fontWeight={700}>{displayTemplateName(tpl.name)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {KEY_LABELS[tpl.key] || tpl.key}
                  {tpl.audioFilename ? ` · ${tpl.audioFilename}` : ''}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
                  {tpl.yemotSynced ? (
                    <Chip
                      size="small"
                      icon={<CheckCircleOutlineIcon />}
                      label="מסונכרן לימות"
                      color="success"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      size="small"
                      icon={<ErrorOutlineIcon />}
                      label="חסרה הקלטה / סנכרון"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {tpl.hasAudio && (
                  <Button
                    size="small"
                    startIcon={playingId === tpl.id ? <StopIcon /> : <PlayArrowIcon />}
                    onClick={() => playAudio(tpl)}
                  >
                    {playingId === tpl.id ? 'עצור' : 'השמע'}
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<UploadFileIcon />}
                  disabled={uploadingId === tpl.id}
                  onClick={() => fileInputRefs.current[tpl.id]?.click()}
                >
                  העלאת קובץ
                </Button>
                <input
                  type="file"
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.webm"
                  hidden
                  ref={(el) => {
                    fileInputRefs.current[tpl.id] = el;
                  }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    onFilePick(tpl, f);
                  }}
                />
                <Button
                  size="small"
                  startIcon={<MicIcon />}
                  disabled={uploadingId === tpl.id}
                  onClick={() => setRecordTpl(tpl)}
                >
                  הקלטה
                </Button>
                {uploadingId === tpl.id && <CircularProgress size={22} />}
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* יצירה */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>תבנית חדשה</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="שם"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>סוג</InputLabel>
              <Select
                label="סוג"
                value={newKey}
                onChange={(e) => {
                  const key = e.target.value;
                  setNewKey(key);
                  if (key === 'registration_approved' && newName === DEFAULT_WIFE_NAME) {
                    setNewName(DEFAULT_REG_NAME);
                  } else if (key === 'wife_exam_encourage' && newName === DEFAULT_REG_NAME) {
                    setNewName(DEFAULT_WIFE_NAME);
                  }
                }}
              >
                <MenuItem value="wife_exam_encourage">פידבק אחרי בחינה</MenuItem>
                <MenuItem value="registration_approved">אישור רישום</MenuItem>
                <MenuItem value="general">כללי</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            ביטול
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newName.trim()}>
            צור
          </Button>
        </DialogActions>
      </Dialog>

      {/* הקלטה */}
      <Dialog
        open={Boolean(recordTpl)}
        onClose={() => uploadingId !== recordTpl?.id && setRecordTpl(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>הקלטת תבנית — {displayTemplateName(recordTpl?.name)}</DialogTitle>
        <DialogContent>
          {recordTpl && (
            <AudioRecorder
              key={recordTpl.id}
              onSave={(blob) => uploadBlob(recordTpl.id, blob, 'recording.webm')}
              saving={uploadingId === recordTpl.id}
              disabled={uploadingId === recordTpl.id}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordTpl(null)} disabled={uploadingId === recordTpl?.id}>
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      {/* בדיקה */}
      <Dialog open={testOpen} onClose={() => !testing && setTestOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {testKind === 'reg' ? 'שיחת בדיקה — אישור רישום' : 'שיחת בדיקה — פידבק בחינה'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="מספר טלפון"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            placeholder="05xxxxxxxx"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)} disabled={testing}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={runTestCall}
            disabled={testing || !testPhone.trim()}
          >
            חייג
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

export default IvrTemplates;
